import { ChangeDetectionStrategy, Component, computed, inject, Input, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ButtonLabels,
  MODAL_TOKEN,
  ModalProvider,
  NAVIGATION_TOKEN,
  NavigationProvider,
  TOAST_TOKEN,
  ToastProvider
} from '@core';
import { take, tap } from 'rxjs';
import { FormatMessageResponse } from 'stream-chat';
import { showInstructionModal } from '../../application/utils';
import {
  ChannelDetailsQueryResult,
  MarkUnreadCommandHandler,
  MuteChannelCommandHandler
} from '../../application/handlers';
import {
  STREAM_CHANNEL_TOKEN,
  STREAM_CHAT_CLIENT_TOKEN,
  StreamChannelProvider,
  StreamChatClientProvider
} from '../../infrastructure/tokens';
import { OrIconComponent } from '@ui-components';

@Component({
  selector: 'lib-manage-channel',
  templateUrl: './manage-channel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [OrIconComponent],
  providers: [MuteChannelCommandHandler, MarkUnreadCommandHandler]
})
export class ManageChannelComponent {
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly navigationProvider: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly toastProvider: ToastProvider = inject(TOAST_TOKEN);
  private readonly muteChannelCommandHandler: MuteChannelCommandHandler = inject(MuteChannelCommandHandler);
  private readonly markUnreadCommandHandler: MarkUnreadCommandHandler = inject(MarkUnreadCommandHandler);
  private readonly channelProvider: StreamChannelProvider = inject(STREAM_CHANNEL_TOKEN);
  private readonly chatClientProvider: StreamChatClientProvider = inject(STREAM_CHAT_CLIENT_TOKEN);

  @Input({ required: true }) channelDetails!: ChannelDetailsQueryResult;
  @Input() onMutedChange?: (isMuted: boolean) => void;

  readonly muteChatLabel: Signal<ButtonLabels> = computed(() => {
    const isMuted: boolean = this.channelDetails?.isMuted;
    const header: string = isMuted ? 'Cofnij wyciszenie czatu' : 'Wycisz czat';
    const icon: string = isMuted ? 'notifications-2' : 'notifications-muted';
    return { header, icon };
  });

  private readonly activeChannelMessages: Signal<unknown[] | undefined> = toSignal(
    this.channelProvider.activeChannelMessages$
  );

  readonly canMarkAsUnread: Signal<boolean> = computed(() => {
    const messages: FormatMessageResponse[] | undefined = this.activeChannelMessages() as
      | FormatMessageResponse[]
      | undefined;
    const lastMessage: FormatMessageResponse | undefined = messages?.[messages.length - 1];
    if (!lastMessage) return false;

    const currentUserId: string | undefined = this.chatClientProvider.getCurrentUserId();
    return lastMessage.user?.id !== currentUserId;
  });

  showInstructionModal(): void {
    this.modalProvider.dismissAllStoredModals();
    showInstructionModal(this.modalProvider);
  }

  muteChannel(): void {
    const newIsMuted: boolean = !this.channelDetails.isMuted;

    this.muteChannelCommandHandler
      .mute(this.channelDetails.channelId, newIsMuted)
      .pipe(
        take(1),
        tap(() => {
          this.onMutedChange?.(newIsMuted);
          this.modalProvider.dismissAllStoredModals();
          this.toastProvider.showToast({
            message: newIsMuted ? 'Wyciszono czat' : 'Cofnięto wyciszenie czatu'
          });
        })
      )
      .subscribe();
  }

  markAsUnread(): void {
    this.markUnreadCommandHandler
      .markAsUnread()
      .pipe(
        take(1),
        tap(() => {
          this.modalProvider.dismissAllStoredModals();
          this.navigationProvider.back();
        })
      )
      .subscribe();
  }
}
